import { ReactNode } from "react";
import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { formatPhone } from "shared/utils/phone";
import { textPx } from "shared/constants/zoom";

// Shared bits for the client/instructor picker Selects. Options carry the
// person's phone so the open dropdown can show it as a muted second line
// (people with short or shared names stay identifiable) and the search box
// matches on it as well as on the name. The closed Select keeps rendering
// the plain `label` (the name alone).
//
// Usage on a Select:
//   options={list.map((p) => ({ value: p.id, label: p.full_name, phone: p.phone_number }))}
//   showSearch
//   filterOption={personFilterOption}
//   optionRender={renderPersonOption}

// Name OR phone match. Phone input tolerates any formatting and the local
// trunk-zero shape ("0812…" finds the stored "+62812…") — same idea as the
// searchType: "phone" table filter in SearchColumnProps.
export const personFilterOption = (
  input: string,
  option?: { label?: unknown; phone?: unknown }
): boolean => {
  if (!option) return false;
  const query = input.trim().toLowerCase();
  if (!query) return true;
  const name = typeof option.label === "string" ? option.label : "";
  if (name.toLowerCase().includes(query)) return true;

  const typedDigits = input.replace(/\D/g, "");
  if (!typedDigits) return false;
  const storedDigits =
    typeof option.phone === "string" ? option.phone.replace(/\D/g, "") : "";
  if (!storedDigits) return false;
  const withoutTrunkZero = typedDigits.replace(/^0+/, "");
  return (
    storedDigits.includes(typedDigits) ||
    (withoutTrunkZero.length > 0 && storedDigits.includes(withoutTrunkZero))
  );
};

// Two-line dropdown option: name over the formatted phone (omitted when the
// person has no stored number). Visual matches the name-over-phone cells
// elsewhere (e.g. the data-migration review table).
export const renderPersonOption = (option: {
  data: { label?: ReactNode; phone?: string | null };
}): ReactNode => (
  <Flex vertical>
    <span>{option.data.label}</span>
    {option.data.phone ? (
      <span style={{ fontSize: textPx(12), color: colors.ink3 }}>
        {formatPhone(option.data.phone)}
      </span>
    ) : null}
  </Flex>
);
